<template>
    <VModalWrapper :title="title" v-on="listeners" v-bind="attributes">
        <div>
            <div class="load" v-if="settingsLoading || sourceLoading">
                <template>
                    <VSpinner></VSpinner>
                </template>
            </div>
            <ValidationObserver v-slot="{ handleSubmit }" v-else>
                <form class="active-form" @submit.prevent="handleSubmit(submit)">
                    <p v-if="sourceError" class="active-form__error">{{ errorMessage }}</p>
                    <p v-if="basedOnRequestId" class="active-form__service-description">
                        Новое обращение на основе №{{ basedOnRequestId }}
                    </p>
                    <p v-if="sourceServiceUnavailable" class="active-form__error">
                        Исходный сервис больше недоступен. Выберите актуальную категорию и сервис.
                    </p>
                    <p
                        v-if="
                            basedOnRequestId &&
                            hasFormFields &&
                            comment &&
                            !(restoredFormValues && restoredFormValues.length)
                        "
                        class="active-form__service-description"
                    >
                        Описание исходного обращения не подходит текущей форме. Заполните её поля заново.
                    </p>
                    <template v-if="selectorType === 'combined'">
                        <ValidationProvider
                            class="active-form__field"
                            name="categoryService"
                            rules="required"
                            v-slot="{ errors }"
                        >
                            <label class="active-form__label active-form__label_required" for="request-categoryService">
                                Категория и сервис
                            </label>

                            <VueSelect
                                id="request-categoryService"
                                label="title"
                                class="custom-select active-form__control"
                                placeholder="Начните вводить для поиска"
                                :options="displayCategoryService"
                                :searchable="true"
                                :filter="categoryServiceFilter"
                                v-model="categoryServiceLocal"
                                @input="routeReplace"
                                :class="{ error: errors[0] }"
                            >
                                <template #option="option">
                                    <span class="request-service-option">
                                        <span class="request-service-option__title">{{ option.title }}</span>
                                        <ServiceFavoriteStar
                                            :favorite="favoriteServiceIds.includes(option.serviceId)"
                                            :busy="favoriteBusyId === option.serviceId"
                                            @toggle="toggleFavorite(option.serviceId)"
                                        />
                                    </span>
                                </template>
                            </VueSelect>

                            <span v-if="categoryService" class="active-form__service-description">
                                {{ categoryService.subtitle }}
                            </span>

                            <span class="active-form__error">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </template>

                    <template v-else>
                        <ValidationProvider
                            class="active-form__field"
                            name="category"
                            rules="required"
                            v-slot="{ errors }"
                        >
                            <label class="active-form__label active-form__label_required" for="request-category">
                                Категория
                            </label>

                            <VSelect
                                id="request-category"
                                name="category"
                                label="title"
                                class="active-form__control"
                                placeholder="Начните вводить категорию для поиска"
                                :class="{ error: errors[0] }"
                                :options="displayCategoriesWithFold"
                                :filter="categoryServiceFilter"
                                @input="routeReplace"
                                v-model="category"
                            ></VSelect>

                            <span class="active-form__error">{{ errors[0] }}</span>
                        </ValidationProvider>

                        <ValidationProvider
                            class="active-form__field"
                            name="service"
                            rules="required"
                            v-slot="{ errors }"
                        >
                            <label class="active-form__label active-form__label_required" for="request-service">
                                Сервис
                            </label>

                            <VueSelect
                                id="request-service"
                                name="service"
                                label="title"
                                class="custom-select active-form__control"
                                placeholder="Начните вводить сервис для поиска"
                                :resetOnOptionsChange="shouldResetServiceSelection"
                                :class="{ error: errors[0] }"
                                :options="category ? favoritesFirst(category.services, favoriteServiceIds) : []"
                                :filter="categoryServiceFilter"
                                @input="routeReplace"
                                v-model="serviceLocal"
                            >
                                <template #option="option">
                                    <span class="request-service-option">
                                        <span class="request-service-option__title">{{ option.title }}</span>
                                        <ServiceFavoriteStar
                                            :favorite="favoriteServiceIds.includes(option.id)"
                                            :busy="favoriteBusyId === option.id"
                                            @toggle="toggleFavorite(option.id)"
                                        />
                                    </span>
                                </template>
                            </VueSelect>

                            <span v-if="service" class="active-form__service-description">
                                {{ service.subtitle }}
                            </span>

                            <span class="active-form__error">{{ errors[0] }}</span>
                        </ValidationProvider>
                    </template>

                    <!-- Только информация: приоритет задаёт сервис, пользователь его не выбирает. -->
                    <div class="active-form__field request-create-priority" v-if="selectedServicePriority !== null">
                        <span class="request-create-priority__label">Приоритет:</span>
                        <RequestPriorityBadge :priority="selectedServicePriority"></RequestPriorityBadge>
                    </div>

                    <VSpinner v-if="formFieldsLoading"></VSpinner>

                    <DynamicRequestForm
                        v-else-if="hasFormFields"
                        ref="dynForm"
                        :fields="formFields"
                        :initial-values="restoredFormValues"
                        @input="onFormValuesInput"
                    ></DynamicRequestForm>

                    <ValidationProvider
                        v-else
                        class="active-form__field"
                        name="description"
                        rules="required"
                        v-slot="{ errors }"
                    >
                        <label class="active-form__label active-form__label_required" for="request-description">
                            Описание
                        </label>

                        <VContenteditable
                            v-model="comment"
                            id="request-description"
                            name="description"
                            @input="routeReplace"
                        ></VContenteditable>

                        <span class="active-form__error">
                            {{ errors[0] }}
                        </span>
                    </ValidationProvider>
                    <VRequestObservers v-model="observersLocal" @input="routeReplace"></VRequestObservers>

                    <div
                        class="file-dropzone"
                        :class="{ 'file-dropzone_active': isDragOver }"
                        @dragenter.prevent="onDragEnter"
                        @dragover.prevent="onDragEnter"
                        @dragleave.prevent="onDragLeave"
                        @drop.prevent="onDrop"
                    >
                        <FileUploader ref="uploader" v-model="files" class="file-dropzone__control">
                            <PaperclipSvg class="file-dropzone__icon"></PaperclipSvg>
                            <span class="file-dropzone__text">
                                Перетащите файлы сюда или
                                <span class="file-dropzone__link">нажмите, чтобы выбрать</span>
                            </span>
                        </FileUploader>
                    </div>
                    <div class="scroll">
                        <FileViewer upload v-model="files" class="request-create-view__file-viewer"></FileViewer>
                    </div>

                    <span v-if="errorMessage && !sourceError" class="active-form__error">{{ errorMessage }}</span>

                    <div class="active-form__actions">
                        <VButton type="button" class="active-form__action button_green-light" @click="hide(true)">
                            Отмена
                        </VButton>

                        <VButton
                            type="submit"
                            class="active-form__action button_green"
                            :disabled="isSubmitting || formFieldsLoading || sourceLoading || sourceError"
                        >
                            Создать обращение
                        </VButton>
                    </div>
                </form>
            </ValidationObserver>
        </div>
    </VModalWrapper>
</template>
<script>
// Components
import VButton from '../components/VButton/VButton.vue';
import VModalWrapper from '../components/VModalWrapper/VModalWrapper.vue';
import VContenteditable from '../components/VContenteditable/VContenteditable.vue';
import { VRequestObservers } from '../components';
import FileViewer from '@/views/components/FileViewer';
import FileUploader from '@/views/components/FileUploader';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import VSelect from '../components/VSelect/VSelect.vue';
import VSpinner from '../components/VProgress/VSpinner.vue';
import DynamicRequestForm from '../components/DynamicRequestForm/DynamicRequestForm.vue';
import PaperclipSvg from '@/images/paperclip.svg';
import RequestPriorityBadge from '@/views/components/RequestPriority/RequestPriorityBadge.vue';
import { isKnownPriority } from '@/js/requestPriority';

// Other
import Component from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import ModalNames from '../../js/enums/ModalNames';
import RequestServiceFormFieldType from '../../js/enums/RequestServiceFormFieldType';
import ModalMixin from '../../js/vue/mixins/ModalMixin';
import { debounce } from 'lodash';
import { fold, filterOptions } from '@/js/textSearch';
import { namespace } from 'vuex-class';
import http from '@/js/http.js';
import { getRequestCopySource } from '@/js/api';
import { favoritesFirst, getServiceFavorites, setServiceFavorite } from '@/js/serviceFavorites';
import ServiceFavoriteStar from '@/views/pages/RequestCreate/components/ServiceFavoriteStar.vue';

const Workspace = namespace('workspace');

@Component({
    components: {
        VSelect,
        VButton,
        VModalWrapper,
        VContenteditable,
        VRequestObservers,
        FileViewer,
        FileUploader,
        VSpinner,
        DynamicRequestForm,
        PaperclipSvg,
        RequestPriorityBadge,
        ServiceFavoriteStar,

        ValidationObserver,
        ValidationProvider,
    },
})
class VRequestCreateModal extends ModalMixin {
    @Ref('uploader')
    uploader;
    @Ref('dynForm')
    dynForm;
    @Prop({ type: String, default: ModalNames.REQUEST_CREATE })
    name;
    @Prop({ type: String, default: 'Создать обращение' })
    defaultTitle;

    @Workspace.State displayCategories;

    isSubmitting = false;
    isDragOver = false;
    dragCounter = 0;
    files = [];
    comment = null;
    observers = null;
    categoryService = null;
    selectorType = 'combined';
    category = null;
    service = null;
    settingsLoading = true;

    formFields = [];
    formValues = [];
    // Values parsed from the URL on open, applied to the dynamic form once its fields load so a
    // shared/reloaded link restores what was filled in. Matched to fields by fieldId (globally
    // unique), so stale values from a different service simply don't apply.
    restoredFormValues = null;
    formFieldsLoading = false;
    formFieldsRequestId = 0;
    errorMessage = '';
    basedOnRequestId = null;
    sourceLoading = false;
    sourceError = false;
    sourceServiceUnavailable = false;
    favoriteServiceIds = [];
    favoriteBusyId = null;

    favoritesFirst = favoritesFirst;

    shouldResetServiceSelection(newOptions, oldOptions, selectedValue) {
        const selectedId = selectedValue[0]?.id;
        return selectedId !== undefined && !newOptions.some((option) => option.id === selectedId);
    }

    async loadFavorites() {
        try {
            this.favoriteServiceIds = await getServiceFavorites();
        } catch (error) {
            this.favoriteServiceIds = [];
            this.$showModal(ModalNames.ERROR, { errorMessage: this.$errorMessage(error) });
        }
    }

    async toggleFavorite(serviceId) {
        if (this.favoriteBusyId !== null) return;
        this.favoriteBusyId = serviceId;
        const favorite = !this.favoriteServiceIds.includes(serviceId);
        try {
            await setServiceFavorite(serviceId, favorite);
            this.favoriteServiceIds = favorite
                ? [...this.favoriteServiceIds, serviceId]
                : this.favoriteServiceIds.filter((id) => id !== serviceId);
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: this.$errorMessage(error) });
        } finally {
            this.favoriteBusyId = null;
        }
    }

    /**
     * The service the request is being created for, whichever selector the account is configured to
     * use. This is what decides whether the service has a custom form attached.
     */
    get selectedServiceId() {
        if (this.selectorType === 'combined') {
            return this.categoryService?.serviceId ?? null;
        }

        return this.service?.id ?? null;
    }

    /**
     * Приоритет, который получит обращение: default_priority выбранного сервиса. Бэкенд
     * копирует его сам при создании — здесь только показывается.
     */
    get selectedServicePriority() {
        const selected = this.selectorType === 'combined' ? this.categoryService : this.service;
        const priority = selected ? selected.defaultPriority : null;

        return isKnownPriority(priority) ? priority : null;
    }

    get hasFormFields() {
        return this.formFields.length > 0;
    }

    async submit() {
        // The form fields may still be loading; submitting now would post a plain comment for a
        // service that actually has a form. The button is disabled in that window, but guard here too.
        if (this.formFieldsLoading) {
            return;
        }
        if (this.sourceLoading || this.sourceError) {
            return;
        }

        this.isSubmitting = true;
        this.errorMessage = '';

        // The custom service form owns its own validation (Vue Formulate). vee-validate's
        // handleSubmit only covers the category/service selectors, so gate on the form here — an
        // unfilled required field must block the request, not post an empty one.
        if (this.hasFormFields) {
            const isFormValid = await this.dynForm.validate();

            if (!isFormValid) {
                this.isSubmitting = false;
                return;
            }
        }

        try {
            const observersValues = this.observers?.map((observer) => observer.value) || [];

            const data = {
                files: this.getFileIds(),
                observers: observersValues,
                serviceId: this.selectedServiceId,
            };
            if (this.basedOnRequestId) {
                data.basedOnRequestId = this.basedOnRequestId;
            }

            if (this.hasFormFields) {
                data.formValues = this.formValues;
            } else {
                data.comment = this.comment;
            }

            const response = await http.post('/requests', data);
            this.$bus.emit('request:created');
            this.resolve(response.data);
            this.hide(true);
        } catch (error) {
            // Keep the modal open and everything the user entered in place. isSubmitting (not the
            // top-level isLoading/settingsLoading) drives this so the form subtree stays mounted —
            // otherwise DynamicRequestForm would unmount and lose the values the user typed.
            this.errorMessage = this.$errorMessage(error);
        } finally {
            this.isSubmitting = false;
        }
    }

    /**
     * Loads the custom form attached to the chosen service, if any. Fetched per selection rather
     * than read from the boot-time catalogue, so a form an admin changed after this tab was opened
     * still submits against its current fields.
     */
    @Watch('selectedServiceId', { immediate: true })
    async loadFormFields(serviceId) {
        // Bump the request id FIRST — before any early return — so that clearing the selection
        // (serviceId -> null) invalidates a still-in-flight fetch for the previous service; without
        // this, that stale response would land and render the old service's form under no selection.
        const requestId = ++this.formFieldsRequestId;
        this.formFields = [];
        this.formValues = [];
        this.errorMessage = '';
        this.formFieldsLoading = false;

        if (!serviceId) {
            return;
        }

        this.formFieldsLoading = true;

        try {
            const { data: service } = await http.get(`/services/${serviceId}?with=formFields`);

            if (requestId !== this.formFieldsRequestId) {
                return;
            }

            this.formFields = service.formFields || [];
        } catch (error) {
            if (requestId === this.formFieldsRequestId) {
                this.errorMessage = this.$errorMessage(error);
            }
        } finally {
            if (requestId === this.formFieldsRequestId) {
                this.formFieldsLoading = false;
            }
        }
    }
    async loadSettings() {
        try {
            const { data } = await http.get('/account-settings');
            this.selectorType = data['request-store.selector-type'] || 'combined';
        } catch (error) {
            //
        } finally {
            this.settingsLoading = false;
        }
    }

    getFileIds() {
        return this.files.filter((file) => file.id && file.id !== -1).map((file) => file.id);
    }

    get categoryId() {
        return parseInt(this.$route.query.categoryId);
    }

    get serviceId() {
        return parseInt(this.$route.query.serviceId);
    }
    get observersLocal() {
        if (!this.observers) {
            this.observers = this.$route.query.observers ? JSON.parse(this.$route.query.observers) : null;
        }
        return this.observers;
    }
    get displayCategoryService() {
        return favoritesFirst(
            this.displayCategories
                .flatMap((category) =>
                    category.services.map((service) => {
                        const title = `${category.title} - ${service.title}`;

                        return {
                            serviceId: service.id,
                            categoryId: category.id,
                            title,
                            // Свёрнутая строка считается один раз здесь, при построении списка, а не на
                            // каждое нажатие клавиши в поиске — см. categoryServiceFilter.
                            foldedTitle: fold(title),
                            subtitle: service.subtitle,
                            defaultPriority: service.defaultPriority,
                            observers: service.observers,
                        };
                    }),
                )
                .map((option, catalogIndex) => ({ ...option, catalogIndex })),
            this.favoriteServiceIds,
        );
    }
    // Раздельный режим (селектор категории/сервиса по отдельности) использует те же категории,
    // что и displayCategories, но с добавленным foldedTitle у каждой категории и её сервисов —
    // по той же причине, что и в displayCategoryService выше. beforeOpen() ниже восстанавливает
    // this.category из ЭТОГО списка, а не из displayCategories, — иначе после восстановления по
    // ссылке из URL сервисы выбранной категории остались бы без foldedTitle и умный поиск в поле
    // «Сервис» молча терял бы слои свёртки/раскладки только в этом сценарии.
    get displayCategoriesWithFold() {
        return this.displayCategories.map((category) => ({
            ...category,
            foldedTitle: fold(category.title),
            services: favoritesFirst(
                category.services.map((service, catalogIndex) => ({
                    ...service,
                    foldedTitle: fold(service.title),
                    catalogIndex,
                })),
                this.favoriteServiceIds,
            ),
        }));
    }
    // Общий :filter для всех трёх VSelect категории/сервиса — опции во всех трёх местах уже
    // несут title/foldedTitle в одинаковой форме (см. displayCategoryService/displayCategoriesWithFold).
    categoryServiceFilter(options, search) {
        const orderedOptions =
            search && options[0]?.catalogIndex !== undefined
                ? options.slice().sort((a, b) => a.catalogIndex - b.catalogIndex)
                : options;
        return filterOptions(orderedOptions, search, {
            getText: (option) => option.title,
            getFoldedText: (option) => option.foldedTitle,
        });
    }
    get categoryServiceLocal() {
        return this.categoryService;
    }
    set categoryServiceLocal(value) {
        if (this.categoryService !== value) {
            this.categoryService = value;
            if (value) this.sourceServiceUnavailable = false;
            this.observers = this.categoryService?.observers
                ? this.categoryService.observers.map((observer) => ({
                      value: observer.id,
                      label: observer.displayName,
                  }))
                : null;
        }
    }
    set observersLocal(value) {
        if (this.observers != value) {
            this.observers = value;
        }
    }
    get serviceLocal() {
        return this.service;
    }
    set serviceLocal(value) {
        if (this.service !== value) {
            this.service = value;
            if (value) this.sourceServiceUnavailable = false;
            this.observers = this.service?.observers
                ? this.service.observers.map((observer) => ({
                      value: observer.id,
                      label: observer.displayName,
                  }))
                : null;
        }
    }

    onFormValuesInput(values) {
        this.formValues = values;
        this.routeReplace();
    }

    /**
     * Serialises the dynamic form's values for the URL. File fields are dropped — a shared link should
     * not carry another person's uploads — and empty values are skipped to keep the URL short.
     *
     * @returns {string|undefined} JSON of the shareable entries, or undefined when there is nothing.
     */
    queryFormValues() {
        if (!this.hasFormFields || !this.formValues.length) {
            return undefined;
        }

        const fileFieldIds = new Set(
            this.formFields.filter((field) => field.type === RequestServiceFormFieldType.FILE).map((field) => field.id),
        );

        const shareable = this.formValues.filter((entry) => {
            if (fileFieldIds.has(entry.fieldId)) {
                return false;
            }

            if (Array.isArray(entry.value)) {
                return entry.value.length > 0;
            }

            return entry.value !== null && entry.value !== undefined && entry.value !== '';
        });

        return shareable.length ? JSON.stringify(shareable) : undefined;
    }

    parseQueryFormValues() {
        const raw = this.$route.query.formValues;

        if (!raw) {
            return null;
        }

        try {
            const parsed = JSON.parse(raw);

            return Array.isArray(parsed) ? parsed : null;
        } catch (error) {
            return null;
        }
    }

    routeReplace() {
        if (!this.debouncedRouteReplace) {
            this.debouncedRouteReplace = debounce(() => {
                const query = {
                    createRequest: true,
                    comment: this.comment,
                    observers: JSON.stringify(this.observers),
                    formValues: this.queryFormValues(),
                };

                if (this.selectorType === 'combined') {
                    query.categoryId = this.categoryService?.categoryId ?? null;
                    query.serviceId = this.categoryService?.serviceId ?? null;
                } else {
                    query.categoryId = this.category?.id ?? null;
                    query.serviceId = this.service?.id ?? null;
                }

                this.$router.replace({ query }).catch(() => {});
                this.debouncedRouteReplace = null;
            }, 500);
        }
        this.debouncedRouteReplace();
    }
    onDragEnter() {
        this.dragCounter++;
        this.isDragOver = true;
    }
    onDragLeave() {
        this.dragCounter = Math.max(0, this.dragCounter - 1);
        if (this.dragCounter === 0) {
            this.isDragOver = false;
        }
    }
    onDrop(event) {
        this.dragCounter = 0;
        this.isDragOver = false;
        this.uploader.submitFiles(event.dataTransfer);
    }
    mounted() {
        this.$bus.on('settings:updated', this.loadSettings);
    }
    shake() {
        const wrapper = document.querySelector('.modal__wrapper');
        wrapper?.classList.add('modal__wrapper--shake');
        setTimeout(() => {
            const wrapper = document.querySelector('.modal__wrapper');
            wrapper?.classList.remove('modal__wrapper--shake');
        }, 1000);
    }

    async beforeOpen(event) {
        await this.loadFavorites();
        this.settingsLoading = true;
        this.errorMessage = '';
        this.basedOnRequestId = event?.params?.basedOnRequestId || null;
        this.sourceError = false;
        this.sourceServiceUnavailable = false;
        this.sourceLoading = !!this.basedOnRequestId;
        // Read synchronously, before the fields load, so DynamicRequestForm has them to seed from.
        this.restoredFormValues = this.parseQueryFormValues();
        let source = null;
        if (this.basedOnRequestId) {
            try {
                const response = await getRequestCopySource(this.basedOnRequestId);
                source = response.data;
                this.restoredFormValues = source.formValues;
            } catch (error) {
                this.sourceError = true;
                this.errorMessage = 'Исходное обращение недоступно. Откройте доступное обращение и повторите попытку.';
            }
        }
        await this.loadSettings();
        if (!this.sourceError) {
            const categoryId = source ? source.categoryId : event?.params?.categoryId ?? this.categoryId;
            const serviceId = source ? source.serviceId : event?.params?.serviceId ?? this.serviceId;
            if (this.selectorType === 'combined') {
                if (serviceId && this.displayCategoryService) {
                    this.categoryService =
                        this.displayCategoryService.find((item) => item.serviceId === serviceId) ?? null;
                }
            } else {
                if (categoryId && this.displayCategories) {
                    this.category = this.displayCategoriesWithFold.find((c) => c.id === categoryId) ?? null;
                }
                if (serviceId && this.category) {
                    this.service = this.category.services.find((s) => s.id === serviceId) ?? null;
                }
            }
            this.sourceServiceUnavailable =
                !!source && !this.displayCategoryService.some((item) => item.serviceId === serviceId);
            this.comment = source ? source.comment : this.$route.query.comment ?? null;
        }
        this.sourceLoading = false;
    }
    beforeDestroy() {
        this.$bus.off('settings:updated', this.loadSettings);
    }
    get hasFormInput() {
        return this.formValues.some((entry) => {
            if (Array.isArray(entry.value)) {
                return entry.value.length > 0;
            }

            return entry.value !== null && entry.value !== '';
        });
    }

    beforeClose(event) {
        let isFullfiled;
        if (this.selectorType === 'combined') {
            isFullfiled =
                this.comment ||
                this.files?.length > 0 ||
                this.categoryService?.serviceId ||
                this.categoryService?.categoryId ||
                this.observers?.length > 0;
        } else {
            isFullfiled =
                this.comment ||
                this.files?.length > 0 ||
                this.category?.id ||
                this.service?.id ||
                this.observers?.length > 0;
        }

        isFullfiled = isFullfiled || this.hasFormInput;

        if (!event?.params?.force && isFullfiled) {
            this.shake();
            event.stop();
            return false;
        }

        this.files = [];
        this.comment = null;
        this.observers = null;
        this.categoryService = null;
        this.category = null;
        this.service = null;
        this.formFields = [];
        this.formValues = [];
        this.restoredFormValues = null;
        this.formFieldsLoading = false;
        this.isSubmitting = false;
        this.errorMessage = '';
        this.basedOnRequestId = null;
        this.sourceError = false;
        this.sourceServiceUnavailable = false;

        const replaceRoute = Object.values(this.$route.query).join('');
        if (replaceRoute) {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    createRequest: undefined,
                    categoryId: undefined,
                    serviceId: undefined,
                    comment: undefined,
                    observers: undefined,
                    formValues: undefined,
                },
            });
        }
        this.debouncedRouteReplace?.cancel();
    }
}

export default VRequestCreateModal;
</script>
<style lang="scss">
.load {
    position: relative;
    top: 30%;
    left: 50%;
}
.active-form {
    &__label {
        font-size: 1rem;
        color: #484848;
    }
    &__empty {
        color: red;
    }
    &__control {
        margin-bottom: 10px;
    }
    &__service-description {
        color: red;
    }
    .scroll {
        overflow-y: auto;
    }
}
.request-service-option {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    white-space: normal;

    &__title {
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
    }
}
.request-create-priority {
    display: flex;
    gap: 8px;
    align-items: center;

    &__label {
        color: #484848;
        font-size: 0.875rem;
    }
}
.request-create-view {
    &__file-viewer {
        max-height: 250px;
    }
}
.file-dropzone {
    margin: 10px 0;
    border-radius: 8px;
    border: 2px dashed $green;
    background-color: rgba(227, 254, 236, 0.4);
    transition: background-color 0.2s, border-color 0.2s;

    &_active {
        background-color: rgba(227, 254, 236, 0.9);
        border-color: $green;
    }

    &__control.button {
        width: 100%;
        padding: 16px 24px;
        box-shadow: none;
        background: none;
        color: $green;

        &:hover,
        &:focus-visible {
            background: rgba(227, 254, 236, 0.6);
        }

        &:active {
            background: rgba(227, 254, 236, 0.9);
        }
    }

    &__icon {
        margin-right: 8px;
        height: 1.125em;
        fill: $green;
    }

    &__link {
        text-decoration: underline;
    }
}
</style>
